import { marked } from 'marked';

const normalize = (value) => String(value || '').replace(/\u00a0/g, ' ').replace(/\s+/g, ' ').trim();
const prefixSource = '(?:(?:[a-z]|\\d+(?:\\.\\d+)*)[.)]|[IVXLCDM]+[ \\t]*[-–—]|[-•])';
const prefixAtEnd = new RegExp(`(?:^|\\s)(${prefixSource}[ \t]*)$`, 'i');
const leadingPrefix = new RegExp(`^${prefixSource}\\s+`, 'i');
const allowed = new Set(['p', 'div', 'span', 'strong', 'b', 'em', 'i', 'br', 'ul', 'ol', 'li', 'blockquote', 'q', 'code', 'pre', 'a', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'table', 'thead', 'tbody', 'tr', 'th', 'td', 'hr']);
const dropped = new Set(['script', 'style', 'iframe', 'object', 'embed', 'svg', 'math', 'template', 'form', 'input', 'img']);
const protectedSelector = 'blockquote,q,code,pre,a';

export const isReportFieldLabelText = (value) => {
  const label = normalize(value).replace(leadingPrefix, '');
  return label.length >= 3 && label.length <= 110 && label.split(' ').length <= 12
    && /^[\p{L}][\p{L}\p{N}\s,&/()“”’'–—-]*:$/u.test(label)
    && !/^(?:https?|ftp|(?:why\s+)?(?:point|pattern|section)\s+\d+|doi|isbn):/i.test(label)
    && !/^(?:if|when|unless|because|although|he|she|they|we|you|i|there|it|this|that)\b/i.test(label)
    && !/\b(?:appears|said|says|felt|feels|would|could|should|must|introduce|suggests|suggest|tends|seems|know|is|are|was|were|has|have)\b/i.test(label);
};

// Only supported tag-shaped entities are decoded; comparisons remain text.
const decode = (raw) => raw.replace(/&(?:amp;)*(?:lt|#0*60|#x0*3c);(\/?(?:strong|b|em|i|p|div|span|br)\b[^<>]*?)&(?:amp;)*(?:gt|#0*62|#x0*3e);/gi, '<$1>');
const sanitize = (root) => {
  Array.from(root.querySelectorAll('*')).forEach((node) => {
    const tag = node.tagName.toLowerCase();
    if (dropped.has(tag)) { node.remove(); return; }
    if (!allowed.has(tag)) { node.replaceWith(...node.childNodes); return; }
    const fieldClass = /^(?:phase5-field-label(?: phase5-field-tone-[0-2])?|phase5-field-list-item|report-field|report-field-body|report-field-space)$/.test(node.className) ? node.className : '';
    const href = tag === 'a' && /^(?:https?:|#)/i.test(node.getAttribute('href') || '') ? node.getAttribute('href') : null;
    const start = tag === 'ol' && /^\d+$/.test(node.getAttribute('start') || '') ? node.getAttribute('start') : null;
    Array.from(node.attributes).forEach((attribute) => node.removeAttribute(attribute.name));
    if (href) node.setAttribute('href', href);
    if (fieldClass) node.className = fieldClass;
    if (start) node.setAttribute('start', start);
  });
};

/** Body-only presentation: no headings, sections, or body ranges are extracted. */
export const formatReportFieldLabelsHtml = (source) => {
  if (typeof DOMParser === 'undefined' || !source) return source || '';
  const doc = new DOMParser().parseFromString(decode(String(source).replace(/\r\n?/g, '\n').replace(/\u00a0/g, ' ')), 'text/html');
  sanitize(doc.body);
  // Inert spans and comments can divide one source token after sanitization.
  // Unwrap only attribute-free spans; preserve semantic and decorated nodes.
  Array.from(doc.body.querySelectorAll('span')).reverse().forEach((node) => {
    if (!node.attributes.length) node.replaceWith(...node.childNodes);
  });
  const comments = doc.createTreeWalker(doc.body, 128);
  const ignored = [];
  while (comments.nextNode()) ignored.push(comments.currentNode);
  ignored.forEach((node) => node.remove());
  doc.body.normalize(); // Adjacent text nodes form a single Markdown token stream.
  const walker = doc.createTreeWalker(doc.body, 4);
  const textNodes = [];
  while (walker.nextNode()) textNodes.push(walker.currentNode);
  textNodes.forEach((node) => {
    if (node.parentElement.closest('code,pre,.phase5-field-label')) return;
    // HTML serializer indentation is not a source paragraph. Collapse only
    // whitespace-only nodes beside block elements so pre-line body styling
    // preserves real Markdown line breaks without doubling HTML paragraph gaps.
    const blockSibling = (sibling) => sibling?.nodeType === 1
      && /^(P|DIV|UL|OL|LI|TABLE|H[1-6]|BLOCKQUOTE)$/.test(sibling.tagName);
    if (!node.nodeValue.trim() && /\n/.test(node.nodeValue)
      && (blockSibling(node.previousSibling) || blockSibling(node.nextSibling))) node.nodeValue = ' ';
    const parsed = doc.createElement('span');
    // Inline Markdown deliberately retains literal list prefixes, including 2).
    parsed.innerHTML = marked.parseInline(node.nodeValue.replace(/\u00a0/g, ' '), { breaks: false });
    sanitize(parsed);
    node.replaceWith(...parsed.childNodes);
  });
  const quoted = (node) => {
    const parent = node.closest('p,div,li') || doc.body;
    const range = doc.createRange(); range.setStart(parent, 0); range.setEndBefore(node);
    const before = range.toString();
    return (before.match(/"/g) || []).length % 2 === 1 || before.lastIndexOf('“') > before.lastIndexOf('”')
      || before.lastIndexOf('‘') > before.lastIndexOf('’') || /(?:^|[\s(])'[^']*$/.test(before);
  };
  const hasBody = (node, remaining = '') => {
    if (remaining.trim()) return true;
    let current = node;
    while (current && current !== doc.body) {
      for (let next = current.nextSibling; next; next = next.nextSibling) {
        if (!next.textContent.trim()) continue;
        return !isReportFieldLabelText(next.textContent);
      }
      current = current.parentElement;
    }
    return false;
  };
  const decorate = (node) => {
    const text = normalize(node.textContent);
    let hash = 0; for (const char of text.toUpperCase()) hash = (hash * 31 + char.charCodeAt(0)) >>> 0;
    node.className = `phase5-field-label phase5-field-tone-${hash % 3}`;
  };
  // A strong token may contain both the label and its value. Split at the
  // first colon with a DOM Range so inline markup and punctuation survive.
  Array.from(doc.body.querySelectorAll('strong,b')).forEach((node) => {
    if (node.closest(protectedSelector + ',.phase5-field-label') || quoted(node)) return;
    const colon = node.textContent.indexOf(':');
    if (colon < 0 || !node.textContent.slice(colon + 1).trim()
      || !isReportFieldLabelText(node.textContent.slice(0, colon + 1))) return;
    const walker = doc.createTreeWalker(node, 4); let remaining = colon + 1;
    while (walker.nextNode()) {
      if (remaining > walker.currentNode.length) { remaining -= walker.currentNode.length; continue; }
      const range = doc.createRange(); range.selectNodeContents(node);
      range.setStart(walker.currentNode, remaining);
      const body = doc.createElement(node.tagName); body.append(range.extractContents());
      node.after(body); break;
    }
  });
  Array.from(doc.body.querySelectorAll('strong,b')).forEach((node) => {
    if (!isReportFieldLabelText(node.textContent) || node.closest(protectedSelector + ',.phase5-field-label') || quoted(node) || !hasBody(node)) return;
    const wrapper = doc.createElement('span');
    const previous = node.previousSibling;
    if (previous?.nodeType === 3) {
      const match = previous.nodeValue.match(prefixAtEnd);
      if (match) {
        previous.nodeValue = previous.nodeValue.slice(0, -match[1].length);
        wrapper.append(doc.createTextNode(match[1]));
      }
    }
    const listItem = node.parentElement;
    if (!wrapper.childNodes.length && listItem.tagName === 'LI' && listItem.parentElement?.tagName === 'OL'
      && Array.from(listItem.childNodes).slice(0, Array.from(listItem.childNodes).indexOf(node)).every((part) => !part.textContent.trim())) {
      const list = listItem.parentElement;
      const ordinal = Number(list.getAttribute('start') || 1) + Array.from(list.children).indexOf(listItem);
      wrapper.append(doc.createTextNode(`${ordinal}. `));
      listItem.classList.add('phase5-field-list-item');
    }
    node.replaceWith(wrapper); wrapper.append(node); decorate(wrapper);
  });
  // Plain fields qualify at structural boundaries. Within a paragraph an
  // uppercase noun phrase is an explicit field signal; sentence/prose and
  // quotation exclusions still apply. This is independent of a label registry.
  const plainWalker = doc.createTreeWalker(doc.body, 4);
  const plainNodes = [];
  while (plainWalker.nextNode()) plainNodes.push(plainWalker.currentNode);
  plainNodes.forEach((node) => {
    if (node.parentElement.closest(protectedSelector + ',strong,b,.phase5-field-label')) return;
    const blockParent = /^(BODY|P|DIV|LI|H[1-6]|TD|TH)$/.test(node.parentElement.tagName);
    const atStart = (!node.previousSibling && blockParent) || node.previousSibling?.nodeName === 'BR';
    const value = node.nodeValue;
    const candidates = [];
    const pattern = new RegExp(`(^|\\n)([ \t]*(?:${prefixSource}[ \t]+)?[^:\\n]{2,110}:)|(?:^|[ \t])((?:${prefixSource}[ \t]+)?[A-Z][A-Z0-9 &/()’'–—-]{1,109}:)`, 'g');
    for (const match of value.matchAll(pattern)) {
      const token = match[2] || match[3];
      const start = match.index + match[0].length - token.length;
      const end = start + token.length;
      if (match[2] && !match[1] && !atStart) continue;
      if (!isReportFieldLabelText(token) || !hasBody(node, value.slice(end))
        || /\d:$/.test(token) && /^\d/.test(value.slice(end))
        || /^\s*\/\//.test(value.slice(end))) continue;
      const range = doc.createRange();
      range.setStart(node.parentElement.closest('p,div,li') || doc.body, 0);
      range.setEnd(node, start);
      const before = range.toString();
      if ((before.match(/"/g) || []).length % 2 || before.lastIndexOf('“') > before.lastIndexOf('”')
        || before.lastIndexOf('‘') > before.lastIndexOf('’') || /(?:^|[\s(])'[^']*$/.test(before)) continue;
      candidates.push({ start, end, token });
    }
    const fragment = doc.createDocumentFragment(); let offset = 0;
    candidates.forEach(({ start, end, token }) => {
      fragment.append(doc.createTextNode(value.slice(offset, start)));
      const label = doc.createElement('span'); label.textContent = token;
      fragment.append(label); decorate(label); offset = end;
    });
    if (offset) { fragment.append(doc.createTextNode(value.slice(offset))); node.replaceWith(fragment); }
  });
  // Valid phrasing content inside P/LI: CSS makes these spans block boxes.
  // Partition only adjacent siblings; never cross a paragraph/section boundary.
  Array.from(doc.body.querySelectorAll('.phase5-field-label')).forEach((label) => {
    if (label.parentElement.classList.contains('report-field')) return;
    const item = label.parentElement;
    if (item.tagName === 'LI' && item.parentElement?.tagName === 'OL'
      && !item.classList.contains('phase5-field-list-item')
      && Array.from(item.childNodes).slice(0, Array.from(item.childNodes).indexOf(label)).every(part => !part.textContent.trim())) {
      const list = item.parentElement;
      const ordinal = Number(list.getAttribute('start') || 1) + Array.from(list.children).indexOf(item);
      label.prepend(doc.createTextNode(`${ordinal}. `)); item.classList.add('phase5-field-list-item');
    }
    const field = doc.createElement('span'); field.className = 'report-field';
    label.replaceWith(field); field.append(label);
    const body = doc.createElement('span'); body.className = 'report-field-body';
    while (field.nextSibling && !(field.nextSibling.nodeType === 1
      && (field.nextSibling.classList.contains('phase5-field-label')
        || /^(P|DIV|UL|OL|LI|H[1-6]|TABLE|BLOCKQUOTE)$/.test(field.nextSibling.tagName)))) {
      body.append(field.nextSibling);
    }
    // Retain source whitespace in the DOM/PDF text stream without letting
    // boundary blank lines add to the controlled CSS field spacing.
    const hideBoundarySpace = (node, leading) => {
      if (node?.nodeType !== 3) return;
      const match = node.nodeValue.match(leading ? /^\s+/ : /\s+$/);
      if (!match) return;
      const space = doc.createElement('span'); space.className = 'report-field-space'; space.textContent = match[0];
      if (leading) { node.nodeValue = node.nodeValue.slice(match[0].length); node.before(space); }
      else { node.nodeValue = node.nodeValue.slice(0, -match[0].length); node.after(space); }
    };
    hideBoundarySpace(body.firstChild, true); hideBoundarySpace(body.lastChild, false);
    if (body.childNodes.length) field.append(body);
  });
  return doc.body.innerHTML;
};

// PDF consumes the same safe presentation tree, never the screen's open state.
export const reportFieldPdfRuns = (source) => {
  const doc = new DOMParser().parseFromString(formatReportFieldLabelsHtml(source), 'text/html');
  const runs = []; let body = '';
  const flush = () => { if (body) runs.push({ text: body, label: false }); body = ''; };
  const walk = (node) => {
    if (node.nodeType === 3) { body += node.nodeValue; return; }
    if (node.nodeType !== 1) return;
    if (node.classList.contains('phase5-field-label')) { flush(); runs.push({ text: node.textContent, label: true }); return; }
    if (node.tagName === 'BR') { body += '\n'; return; }
    Array.from(node.childNodes).forEach(walk);
    if (/^(P|DIV|LI|H[1-6]|TR)$/.test(node.tagName)) body += '\n';
  };
  Array.from(doc.body.childNodes).forEach(walk); flush(); return runs;
};

// List numbers are visible CSS markers on screen; make them literal PDF text
// before the existing PDF line renderer removes the enclosing HTML tags.
export const preserveReportListMarkersHtml = (source) => {
  if (!/<(?:ol|ul)\b/i.test(source)) return source;
  const doc = new DOMParser().parseFromString(source, 'text/html');
  doc.querySelectorAll('ol,ul').forEach((list) => {
    Array.from(list.children).filter((node) => node.tagName === 'LI').forEach((item, index) => {
      const prefix = list.tagName === 'OL' ? `${Number(list.getAttribute('start') || 1) + index}. ` : '• ';
      item.insertBefore(doc.createTextNode(prefix), item.firstChild);
    });
  });
  return doc.body.innerHTML;
};
